/**
 * Utilitaires pour détecter la plateforme (Web vs Android App)
 */

/**
 * Détecte si l'application s'exécute dans l'app Android
 * @returns {boolean} true si on est dans l'app Android, false sinon
 */
export function isAndroidApp() {
  if (typeof window === 'undefined') return false
  
  // Vérifier le User-Agent pour l'identifiant de l'app Android
  const userAgent = navigator.userAgent || ''
  
  // L'app Android ajoute "OrdonnanceurApp Mobile" au User-Agent
  // Voir MainActivity.java ligne 54
  return userAgent.includes('OrdonnanceurApp Mobile')
}

/**
 * Détecte si l'application s'exécute sur le web (navigateur)
 * @returns {boolean} true si on est sur le web, false sinon
 */
export function isWeb() {
  return !isAndroidApp()
}

/**
 * Retourne le nom de la plateforme actuelle
 * @returns {'android' | 'web'}
 */
export function getPlatform() {
  return isAndroidApp() ? 'android' : 'web'
}

/**
 * Retourne l'URL de base de l'API selon la plateforme
 * @returns {string} URL de base de l'API
 */
export function getApiBaseUrl() {
  // En mode développement Android, essayer d'utiliser le proxy Vite si disponible
  // Sinon, utiliser l'URL directe du backend
  if (isAndroidApp()) {
    // En développement, essayer d'utiliser le proxy Vite pour de meilleures performances
    // Le proxy Vite est accessible via l'URL du frontend
    if (import.meta.env.DEV) {
      // Essayer d'utiliser le proxy Vite si le frontend est accessible
      // L'URL du frontend est généralement http://IP:3000, donc le proxy est /api
      const useProxy = import.meta.env.VITE_ANDROID_USE_PROXY !== 'false'
      
      if (useProxy) {
        // Utiliser le proxy Vite (plus rapide car sur la même machine)
        const proxyUrl = '/api'
        
        if (import.meta.env.DEV) {
          console.log('📱 App Android détectée - Utilisation du proxy Vite')
          console.log('🔗 URL de l\'API (proxy):', proxyUrl)
          console.log('💡 Pour utiliser l\'URL directe, définissez VITE_ANDROID_USE_PROXY=false')
        }
        
        return proxyUrl
      }
    }
    
    // Fallback : même backend que le web en prod (VITE_API_URL), sinon IP locale pour dev
    const androidUrl = import.meta.env.VITE_ANDROID_API_URL || import.meta.env.VITE_API_URL || 'http://192.168.1.79:5000/api'
    
    // Log pour le débogage
    if (import.meta.env.DEV) {
      console.log('📱 App Android détectée - Utilisation de l\'URL directe')
      console.log('🔗 URL de l\'API:', androidUrl)
      console.log('💡 Pour utiliser le proxy Vite, ne définissez pas VITE_ANDROID_USE_PROXY=false')
    }
    
    return androidUrl
  }
  
  // Pour le web :
  // - en DEV, utiliser systématiquement le proxy Vite (/api → target http://localhost:5000) pour éviter les confusions
  // - en PROD, utiliser VITE_API_URL si défini (sinon fallback localhost)
  const webUrl = import.meta.env.DEV
    ? '/api'
    : (import.meta.env.VITE_API_URL || 'http://localhost:5000/api')
  
  // Log pour le débogage
  if (import.meta.env.DEV) {
    console.log('🌐 Mode Web détecté')
    console.log('🔗 URL de l\'API:', webUrl)
  }
  
  return webUrl
}

